import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import schedule from '../data/schedule';
import { useUser } from '../context/UserContext';
import { pick, scheduleText, MONTHS, WEEKDAYS } from '../i18n';


const ADMIN_PIN = '12061';

export default function Schedule({ lang = 'en' }) {
  const { user } = useUser();
  const name = user?.name || '';
  const userId = user?.id || null;

  const [view, setView] = React.useState('calendar');
  const [completedDates, setCompletedDates] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-CA'); // local date, not UTC
  // The calendar is browsable across years; the schedule itself repeats, so a
  // different year re-renders the same readings against that year's weekdays.
  const thisYear = today.getFullYear();
  const [currentYear, setCurrentYear] = React.useState(thisYear);
  const navigate = useNavigate();
  const todayRef = React.useRef(null);

  const [showBulk, setShowBulk] = React.useState(false);
  const [bulkPin, setBulkPin] = React.useState('');
  const [bulkUnlocked, setBulkUnlocked] = React.useState(false);
  const [bulkName, setBulkName] = React.useState('');
  const [bulkFrom, setBulkFrom] = React.useState('');
  const [bulkTo, setBulkTo] = React.useState(todayStr);
  const [bulkPortion, setBulkPortion] = React.useState('both');
  const [bulkStatus, setBulkStatus] = React.useState('');
  const [bulkRunning, setBulkRunning] = React.useState(false);

  // Keep bulkName in sync when user logs in
  React.useEffect(() => { if (name) setBulkName(name); }, [name]);

  const isZh = lang === 'zh' || lang === 'sc';
  const t = pick(scheduleText, lang);
  const months = pick(MONTHS, lang);
  const weekdays = pick(WEEKDAYS, lang);

  // Reload dots whenever logged-in user changes
  React.useEffect(() => { loadCompletedDates(); }, [name]);

  React.useEffect(() => {
    if (!loading && todayRef.current) {
      setTimeout(() => todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [loading, view]);

  async function loadCompletedDates() {
    setLoading(true);
    if (name) {
      // By account where we have one, so a rename keeps the calendar intact;
      // by name otherwise, for rows written before the migration.
      const q = supabase.from('checkins').select('date,portion');
      const { data } = userId
        ? await q.or(`user_id.eq.${userId},and(user_id.is.null,name.ilike.${name})`)
        : await q.ilike('name', name);
      if (data) {
        const map = {};
        data.forEach(r => {
          if (!map[r.date]) map[r.date] = { nt: false, ot: false };
          if (r.portion === 'NT') map[r.date].nt = true;
          if (r.portion === 'OT') map[r.date].ot = true;
        });
        setCompletedDates(map);
      }
    } else {
      setCompletedDates({});
    }
    setLoading(false);
  }

  // Returns { nt: bool, ot: bool } for a date
  function getDotState(dateStr) {
    return completedDates[dateStr] || { nt: false, ot: false };
  }

  function goToDay(dateStr) {
    navigate(dateStr === todayStr ? '/reading' : `/reading?date=${dateStr}`);
  }

  function handlePinSubmit() {
    if (bulkPin === ADMIN_PIN) { setBulkUnlocked(true); setBulkStatus(''); }
    else setBulkStatus('pin-error');
  }

  async function runBulkComplete() {
    if (!bulkName.trim() || !bulkFrom || !bulkTo) return;
    setBulkRunning(true);
    setBulkStatus('');

    // Resolve the typed name to an account once, so the rows carry an id.
    // Unknown names still record by name alone rather than being rejected.
    const { data: acct } = await supabase.rpc('account_ids_for_names', { p_names: [bulkName.trim()] });
    const bulkUserId = acct?.[0]?.id ?? null;

    const from = new Date(bulkFrom + 'T12:00:00');
    const to   = new Date(bulkTo   + 'T12:00:00');
    const rows = [];

    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const dateStr = `${d.getFullYear()}-${mm}-${dd}`;
      const schedKey = `${mm}-${dd}`;
      if (!schedule[schedKey]) continue;
      const portions = bulkPortion === 'both' ? ['NT', 'OT'] : bulkPortion === 'nt' ? ['NT'] : ['OT'];
      // Bulk complete names someone explicitly, which may not be the signed-in
      // reader, so resolve that name to an account rather than assuming.
      for (const portion of portions) {
        rows.push({ name: bulkName.trim(), user_id: bulkUserId, date: dateStr, portion });
      }
    }

    if (rows.length === 0) { setBulkStatus('none'); setBulkRunning(false); return; }

    let inserted = 0;
    for (let i = 0; i < rows.length; i += 100) {
      const { error } = await supabase.from('checkins')
        .upsert(rows.slice(i, i + 100), { onConflict: 'name,date,portion', ignoreDuplicates: true });
      if (!error) inserted += rows.slice(i, i + 100).length;
    }

    setBulkStatus(`success:${inserted}`);
    setBulkRunning(false);
    if (bulkName.trim().toLowerCase() === name.toLowerCase()) await loadCompletedDates();
  }

  function resetBulk() {
    setShowBulk(false);
    setBulkPin('');
    setBulkUnlocked(false);
    setBulkName(name);
    setBulkFrom('');
    setBulkTo(todayStr);
    setBulkPortion('both');
    setBulkStatus('');
  }

  // ── Calendar render ────────────────────────────────────────────────────────

  function renderCalendar() {
    return months.map((monthName, monthIndex) => {
      const daysInMonth = new Date(currentYear, monthIndex + 1, 0).getDate();
      const firstDay = new Date(currentYear, monthIndex, 1).getDay();
      const cells = [];
      for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="cal-day empty" />);

      for (let d = 1; d <= daysInMonth; d++) {
        const mm = String(monthIndex + 1).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        const dateStr = `${currentYear}-${mm}-${dd}`;
        const hasReading = !!schedule[`${mm}-${dd}`];
        const isToday = dateStr === todayStr;
        const dot = getDotState(dateStr);
        const hasDot = dot.nt || dot.ot;
        const isPast = new Date(dateStr + 'T12:00:00') < today;

        cells.push(
          <div key={d} ref={isToday ? todayRef : null}
            className={[
              'cal-day',
              isToday ? 'cal-today' : '',
              !hasReading ? 'cal-rest' : '',
              isPast && hasReading && !hasDot ? 'cal-missed' : '',
              hasReading ? 'cal-clickable' : '',
            ].join(' ')}
            onClick={() => hasReading && goToDay(dateStr)}>
            <span className="cal-num">{d}</span>
            {/* Show two separate dots — teal for NT, orange for OT */}
            {hasReading && (dot.nt || dot.ot) && (
              <span className="cal-dots-row">
                <span className={`cal-dot ${dot.nt ? 'dot-nt' : 'dot-empty'}`} />
                <span className={`cal-dot ${dot.ot ? 'dot-ot' : 'dot-empty'}`} />
              </span>
            )}
          </div>
        );
      }

      return (
        <div className="cal-month" key={monthIndex}>
          <div className="cal-month-name">{monthName}</div>
          <div className="cal-grid">
            {weekdays.map((d, i) => <div key={i} className="cal-weekday">{d}</div>)}
            {cells}
          </div>
        </div>
      );
    });
  }

  function renderList() {
    const allDays = [];
    for (let m = 0; m < 12; m++) {
      const daysInMonth = new Date(currentYear, m + 1, 0).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const mm = String(m + 1).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        const dateStr = `${currentYear}-${mm}-${dd}`;
        if (!schedule[`${mm}-${dd}`]) continue;
        const isToday = dateStr === todayStr;
        const dot = getDotState(dateStr);
        const label = new Date(dateStr + 'T12:00:00').toLocaleDateString(
          isZh ? 'zh-TW' : lang === 'es' ? 'es-ES' : 'en-US',
          { month: 'long', day: 'numeric' }
        );
        allDays.push(
          <div key={dateStr} ref={isToday ? todayRef : null}
            className={`list-row ${isToday ? 'list-today' : ''}`}
            onClick={() => goToDay(dateStr)}>
            <span className="list-date">{label}</span>
            <div className="list-dots">
              <span className={dot.nt ? 'check-nt' : ''} style={!dot.nt ? { color: '#374151' } : {}}>
                {dot.nt ? '✓' : '✗'}
              </span>
              <span className={dot.ot ? 'check-ot' : ''} style={!dot.ot ? { color: '#374151' } : {}}>
                {dot.ot ? '✓' : '✗'}
              </span>
            </div>
          </div>
        );
      }
    }
    return allDays;
  }

  if (loading) return <div className="page"><div className="loading">{t.loading}</div></div>;

  return (
    <div className="page">
      <div className="schedule-header">
        <div className="year-nav">
          <button className="year-arrow" onClick={() => setCurrentYear(y => y - 1)}
                  aria-label={t.prevYear} title={t.prevYear}>←</button>
          <select className="year-jump" value={currentYear}
                  onChange={e => setCurrentYear(Number(e.target.value))}
                  aria-label={t.jumpYear}>
            {/* A decade either side is more than anyone needs and keeps the list scannable. */}
            {Array.from({ length: 21 }, (_, i) => thisYear - 10 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button className="year-arrow" onClick={() => setCurrentYear(y => y + 1)}
                  aria-label={t.nextYear} title={t.nextYear}>→</button>
          {currentYear !== thisYear && (
            <button className="year-today" onClick={() => setCurrentYear(thisYear)}>{t.today}</button>
          )}
        </div>
        <h1>{t.title(currentYear)}</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="cal-legend">
            <span><span className="cal-dot dot-nt" /> {t.nt}</span>
            <span><span className="cal-dot dot-ot" /> {t.ot}</span>
          </div>
          <button
            onClick={() => setShowBulk(b => !b)}
            style={{
              background: showBulk ? 'var(--border)' : 'var(--brand-btn)',
              border: 'none', borderRadius: 8, color: 'white',
              padding: '6px 14px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
            }}>
            📋 {t.bulk}
          </button>
          <button className="view-toggle" onClick={() => setView(v => v === 'calendar' ? 'list' : 'calendar')}>
            {view === 'calendar' ? t.listView : t.calView}
          </button>
        </div>
      </div>

      {/* Bulk complete panel */}
      {showBulk && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, padding: 20, marginBottom: 24,
        }}>
          {!bulkUnlocked ? (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--brand)', marginBottom: 12 }}>
                🔒 {t.bulk} — {t.pinLabel}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="password" placeholder={t.pinPlaceholder} value={bulkPin}
                  onChange={e => setBulkPin(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePinSubmit()}
                  style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', width: 160 }} />
                <button onClick={handlePinSubmit}
                  style={{ background: 'var(--brand-btn)', border: 'none', borderRadius: 8, color: 'white', padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>
                  {t.unlock}
                </button>
                <button onClick={resetBulk}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)', padding: '8px 12px', cursor: 'pointer' }}>
                  {t.cancel}
                </button>
              </div>
              {bulkStatus === 'pin-error' && (
                <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 8 }}>⚠️ {t.pinError}</div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--brand)', marginBottom: 16 }}>🔓 {t.bulk}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t.nameLabel}</label>
                  <input value={bulkName} onChange={e => setBulkName(e.target.value)}
                    style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t.fromLabel}</label>
                  <input type="date" value={bulkFrom} onChange={e => setBulkFrom(e.target.value)}
                    style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t.toLabel}</label>
                  <input type="date" value={bulkTo} onChange={e => setBulkTo(e.target.value)}
                    style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t.portionLabel}</label>
                  <select value={bulkPortion} onChange={e => setBulkPortion(e.target.value)}
                    style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', color: 'var(--text)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}>
                    <option value="both">{t.portionBoth}</option>
                    <option value="nt">{t.portionNT}</option>
                    <option value="ot">{t.portionOT}</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={runBulkComplete}
                  disabled={bulkRunning || !bulkName.trim() || !bulkFrom || !bulkTo}
                  style={{ background: 'var(--brand-btn)', border: 'none', borderRadius: 8, color: 'white', padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', opacity: bulkRunning || !bulkName.trim() || !bulkFrom || !bulkTo ? 0.5 : 1 }}>
                  {bulkRunning ? t.running : t.run}
                </button>
                <button onClick={resetBulk}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)', padding: '10px 16px', cursor: 'pointer' }}>
                  {t.cancel}
                </button>
                {bulkStatus.startsWith('success') && (
                  <span style={{ color: '#34d399', fontWeight: 600, fontSize: '0.9rem' }}>
                    ✅ {t.success} ({bulkStatus.split(':')[1]} records)
                  </span>
                )}
                {bulkStatus === 'none' && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No reading days in that range.</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'calendar'
        ? <div className="cal-year">{renderCalendar()}</div>
        : <div className="list-view">{renderList()}</div>
      }
    </div>
  );
}